import { ChangeEvent, useState } from "react";
import { Button, Modal, TextInput } from "@monolith-forensics/monolith-ui";
import ButtonMenu from "../Forms/components/ButtonMenu.js";
import { usePermissions } from "../../hooks/usePermissions";
import LoggingAPI from "../../api/logging/index.js";
import { deleteCase } from "@/api/index.js";
import { useSnackbar } from "notistack";
import { Case } from "@/pages/Cases/types/Cases.js";

interface DeleteCaseProps {
  show: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onCancel: () => void;
  buttonProps?: {
    cancelText: string;
    submitText: string;
  };
  defaultInfo: Case | null | undefined;
}

export const DeleteCaseModal: React.FC<DeleteCaseProps> = ({
  show,
  onClose,
  onSubmit,
  onCancel,
  buttonProps,
  defaultInfo,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const [deleteDisabled, setDeleteDisabled] = useState(true);

  if (!defaultInfo) {
    enqueueSnackbar(`Error deleting case. Please try again`, {
      variant: "error",
    });
    throw new Error("case info value is null or undefined");
  }

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await deleteCase(defaultInfo?.case_id);
      enqueueSnackbar(`Case ${defaultInfo?.case_number} Deleted.`, {
        variant: "success",
      });

      LoggingAPI.createLog({
        user_id: currentUser.user_id,
        message: `Deleted Case: ${defaultInfo?.case_number} - ${defaultInfo?.case_ref}`,
        event: "case:delete",
        object_type: "case",
        object_uuid: defaultInfo?.uuid,
      });

      onSubmit?.();
    } catch (error) {
      console.error("Error deleting case:", error);
      enqueueSnackbar(`Error deleting case. Please try again`, {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
      onClose?.();
    }
  };

  const handleCancel = () => {
    onCancel?.();
  };

  return (
    <Modal
      open={show}
      onClose={onClose}
      style={{ maxWidth: "500px", marginTop: "10vh" }}
    >
      <Modal.Title>Delete Case</Modal.Title>
      <div style={{ fontWeight: 500, margin: 0 }}>
        <p>
          All data associated with the case{" "}
          <span style={{ fontWeight: 800, fontSize: "14px" }}>
            {defaultInfo.case_ref} [{defaultInfo.case_number}]
          </span>{" "}
          will be deleted. This action is permanent and cannot be undone.
        </p>
        <div>
          <p>
            Please type "
            <span style={{ fontWeight: 800, fontSize: "14px" }}>
              {`${defaultInfo.case_number}`}
            </span>
            " to confirm
          </p>
          <TextInput
            size="sm"
            variant="outlined"
            data-testid="case-delete-confirm-input"
            placeholder={`${defaultInfo.case_number}`}
            description={`Type ${defaultInfo.case_number} to confirm`}
            width="305px"
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              e.target.value === `${defaultInfo.case_number}`
                ? setDeleteDisabled(false)
                : setDeleteDisabled(true);
            }}
          />
        </div>
      </div>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          {buttonProps?.cancelText || "Cancel"}
        </Button>
        <Button
          size="xs"
          color="error"
          variant="contained"
          onClick={handleSubmit}
          disabled={deleteDisabled || submitting}
          loading={submitting}
          data-testid="case-delete-confirm-button"
        >
          {buttonProps?.submitText || "Delete Case"}
        </Button>
      </ButtonMenu>
    </Modal>
  );
};
